// Web-side mirror of apps/api/src/lib/utilityProviders.ts (§10.4).
//
// Workspace boundary: apps/web cannot import from apps/api. The provider
// constants are duplicated here so that buildUtilityContactsTable() in
// leaseConfig.ts can resolve `tenant_direct` utilities to provider name +
// phone without crossing workspaces. Keep the two files byte-identical
// (modulo this header). Verified via diff in CI (or grep during reviews)
// is the contract for now; a shared workspace would be a larger refactor.

/**
 * A utility-service provider. `name` appears in the contacts table left
 * column; `phone` follows the em-dash.
 */
export interface UtilityProvider {
  name: string;
  phone: string;
  /** Logo asset filename under lease_render/assets/ (synced to S3); rendered
   *  inline in the UTILITY CONTACTS table. */
  logo: string;
}

export const PACIFIC_POWER: UtilityProvider = {
  name: 'Pacific Power',
  phone: '888.221.7070',
  logo: 'logo-pacific-power.svg',
};

export const COLUMBIA_REA: UtilityProvider = {
  name: 'Columbia REA',
  phone: '509.529.7119',
  logo: 'logo-columbia-rea.svg',
};

export const CASCADE_NATURAL_GAS: UtilityProvider = {
  name: 'Cascade Natural Gas',
  phone: '888.522.1130',
  logo: 'logo-cascade-natural-gas.png',
};

export const BDI_GARBAGE: UtilityProvider = {
  name: 'BDI',
  phone: '509.547.2476',
  logo: 'logo-bdi.svg',
};

/**
 * Resolve a `UtilityProvider` for a tenant-direct utility.
 *
 * - `Electricity` is routed by AppFolio's `Electrical Provider` custom field
 *   (`'Pacific Power'` or `'Columbia REA'`). Missing or unrecognized values
 *   default to PACIFIC_POWER (caller is responsible for warn-logging the
 *   default per §10.5; this helper does not log).
 * - `Gas` always returns CASCADE_NATURAL_GAS when tenant-direct.
 * - `Garbage` is routed by AppFolio's property `Garbage Provider` field
 *   (passed as `garbageProvider`): a Basin/BDI value → BDI_GARBAGE; anything
 *   else — including missing/unknown or a city value — returns null so the
 *   caller stays GENERAL (no specific hauler named) rather than hardcoding BDI
 *   for a property whose hauler AppFolio doesn't confirm.
 * - `Water` and `Sewer` are never tenant-direct in WHP's market (always
 *   billed through Lessor or the city). If they appear here, returns null
 *   so the caller can decide how to surface (likely a "TBD" row or a
 *   suppressed entry).
 *
 * The utility name match is case-insensitive on the leading token so
 * "electricity", "Electric", "ELECTRICITY", etc. all resolve.
 */
export function providerFor(
  utilityName: string,
  electricalProvider: 'Pacific Power' | 'Columbia REA' | null,
  garbageProvider?: string | null,
): UtilityProvider | null {
  const lower = utilityName.trim().toLowerCase();
  if (lower.startsWith('electric')) {
    if (electricalProvider === 'Columbia REA') return COLUMBIA_REA;
    return PACIFIC_POWER;
  }
  if (lower.startsWith('gas')) return CASCADE_NATURAL_GAS;
  if (lower.startsWith('garbage')) {
    const gp = (garbageProvider ?? '').trim().toLowerCase();
    return gp.includes('basin') || gp.includes('bdi') ? BDI_GARBAGE : null;
  }
  return null;
}
